import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppConfig } from '../app.config';
import { EnvConfig } from '../env-config';
import { AnsibleBoolean } from '../interfaces/ansible-config';
import { NetworkRequestOptions } from '../interfaces/request';
import { AuthService } from '../../auth/services/auth.service';
import { RxRequestService } from './rx-request.service';

export let _teamSettingsService: TeamSettingsService | undefined;

declare const __RBAC_ENABLED__: string;
declare const __DEPLOYMENT_ENV__: string;
declare const __H2O_ENABLED__: AnsibleBoolean;
declare const __HEVO_UI_POST_LOAD_TRANSFORMATION_ENABLED__: AnsibleBoolean;
declare const __HEVO_UI_CEREBRA_ENABLED__: AnsibleBoolean;
declare const __HEVO_UI_UNIFIED_VIEW_ENABLED__: AnsibleBoolean;

export class TeamSettingsService {
  static deps = [AppConfig, AuthService, RxRequestService];

  private _teamSettingsUrl = this._appConfig.getTeamSettingsURL();
  private _teamSettingsSubject$ = new BehaviorSubject<any>(null);

  constructor(
    private _appConfig: AppConfig,
    private _authService: AuthService,
    private _rxRequestService: RxRequestService
  ) {
    _teamSettingsService = this;

    this._authService.logoutSubject
      .pipe(
        tap(() => {
          this._teamSettingsSubject$.next(null);
        })
      )
      .subscribe();
  }

  getTeamSettingsValue() {
    return this._teamSettingsSubject$.getValue();
  }

  getTeamSettings(showLoading = true, showErrorMsg = true, handleUnauthorisedResponse = true) {
    const options = {
      uiOptions: {
        handleUnauthorisedResponse: handleUnauthorisedResponse,
        showLoading,
        showErrorMsg
      }
    };

    return this._rxRequestService.get(this._teamSettingsUrl, options).pipe(
      tap(res => {
        this._teamSettingsSubject$.next(res.data);
      })
    );
  }

  applyTeamSettings(params: any, successMsg?: string): Observable<void> {
    const options: NetworkRequestOptions = {
      uiOptions: {
        showLoading: false,
        showErrorMsg: false,
        successMsg
      }
    };

    return this._rxRequestService.put(this._teamSettingsUrl, options, params);
  }

  isDbtEnabled() {
    return this.getTeamSettingsValue()?.['dbt_enabled_config'];
  }

  isRBACEnabled() {
    return (
      __RBAC_ENABLED__ === AnsibleBoolean.TRUE && this.getTeamSettingsValue()?.['rbac_enabled']
    );
  }

  isTransformValidationEnabled() {
    return this.getTeamSettingsValue()?.['py_transform_validation_enabled'];
  }

  isActivationEnabled() {
    return this.getTeamSettingsValue()?.['activate_enabled'];
  }

  isFBAdsSearchEnabled() {
    return this.getTeamSettingsValue()?.['fb_search_mode'];
  }

  isMongoDbDisplayParseAsStringOptionEnabled() {
    return this.getTeamSettingsValue()?.['mongo_display_parse_as_string_option'];
  }

  isWebhookEventsEnabled() {
    return this.getTeamSettingsValue()?.['enable_hevo_webhook_events'];
  }

  isWorkflowsEnabled() {
    return this.getTeamSettingsValue()?.['is_workflow_enabled'];
  }

  isDraftPipelineEnabled() {
    // Unified view retires Standard draft pipelines in the UI: no draft is
    // saved on back/route-change/logout, no draft resume, no draft rows fetched.
    // The unified listing backend excludes existing draft rows on its side.
    if (this.isUnifiedPipelineViewEnabled()) {
      return false;
    }

    return this.getTeamSettingsValue()?.['draft_pipeline_enabled'];
  }

  getVPCPeeringConfig() {
    const hostData = this.getTeamSettingsValue()?.['vpc_peering_host'];

    let host = null;

    if (hostData) {
      try {
        const hostRegionMapping = JSON.parse(hostData);
        host = hostRegionMapping[__DEPLOYMENT_ENV__];
      } catch (e) {
        host = hostData;
      }
    }

    return {
      enabled: !!host,
      host
    };
  }

  isHistoricalTaskSeparationForTableModeEnabled() {
    const teamSettings = this.getTeamSettingsValue();

    return teamSettings?.['team_version'] >= 10
      ? true
      : teamSettings?.['historical_task_separation_for_table_mode'] || false;
  }

  isServerlessAllowedForTeam() {
    return !!this.getTeamSettingsValue()?.['is_redshift_serverless_enabled'];
  }

  isH2OEnabled() {
    const teamSettings = this.getTeamSettingsValue();
    return (
      typeof __H2O_ENABLED__ !== 'undefined' &&
      __H2O_ENABLED__ === 'True' &&
      (teamSettings?.['active_products'] || []).includes('hevo_2')
    );
  }

  isPostLoadTransformationEnabled() {
    return typeof __HEVO_UI_POST_LOAD_TRANSFORMATION_ENABLED__ !== 'undefined' && __HEVO_UI_POST_LOAD_TRANSFORMATION_ENABLED__ === 'True';
  }

  /** Deployment-level (Ansible build) gate for the Cerebra chatbot. Combined with the
   *  per-user enabled API before the widget is shown. */
  isCerebraEnabled() {
    return typeof __HEVO_UI_CEREBRA_ENABLED__ !== 'undefined' && __HEVO_UI_CEREBRA_ENABLED__ === 'True';
  }

  isH20BillingEnabled() {
    const teamSettings = this.getTeamSettingsValue();
    return (
      this.isH2OEnabled() &&
      EnvConfig.isH20FeatureEnabled('BILLING') &&
      teamSettings?.['enable_hevo_2_billing']
    );
  }

  isEdgeAsDefaultEnabled() {
    const teamSettings = this.getTeamSettingsValue();
    return teamSettings?.['edge_as_default'];
  }

  /**
   * Unified pipeline listing (Edge + Standard in one table). Enabled only when
   * BOTH the region-level build flag `__HEVO_UI_UNIFIED_VIEW_ENABLED__` (Ansible)
   * and the per-team `unified_view_pipeline` setting are on — the region flag is
   * the deployment gate, the team setting the per-team rollout switch.
   */
  isUnifiedPipelineViewEnabled() {
    const isRegionEnabled =
      typeof __HEVO_UI_UNIFIED_VIEW_ENABLED__ !== 'undefined' &&
      __HEVO_UI_UNIFIED_VIEW_ENABLED__ === 'True';

    return isRegionEnabled && !!this.getTeamSettingsValue()?.['unified_view_pipeline'];
  }

  isWorkspacesEnabled() {
    const teamSettings = this.getTeamSettingsValue();
    return !!teamSettings?.['isv_enabled'];
  }

  isYukiProxyForSnowflakeEnabled() {
    return this.getTeamSettingsValue()?.['enable_yuki_proxy_for_snowflake'] === true;
  }

  isConnectivityServiceEnabled() {
    return this.getTeamSettingsValue()?.['connectivity_service_enabled'] === true;
  }

  shouldShowCycleMigrationBanner() {
    return !!this.getTeamSettingsValue()?.['should_show_cycle_migration_banner'];
  }

  showPubKeyMessageTeam() {
    return !!this.getTeamSettingsValue()?.['show_pub_key_message_team'];
  }

  isPythonTransformationsEnabled() {
    return !!this.getTeamSettingsValue()?.['python_transformations_enabled'];
  }

  /**
   * Per HEVO-19714, derived server-side per caller. True when the caller can create/edit Python
   * transformations — Hevo employees are always allowed; customer sessions require the team's
   * python_transformation_customer_edit_allowed flag to be on. See TeamSettingsResource on the
   * backend for the exact rule.
   */
  isPythonEditAllowed() {
    return !!this.getTeamSettingsValue()?.['python_edit_allowed'];
  }
}
